import { Icon as OldIcon } from "@/features/icons/components/IconLoader"
import { cn } from "@/utils/helpers"
// import { ImageWithFallback } from "@/utils/ImageWithFallback"
import { ImageWithFallback } from "@/core/components/ImageWithFallback"
import { faStar } from "@awesome.me/kit-44b29310a6/icons/classic/solid"
import { Tooltip } from "antd"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { Icon } from "./icon/Icon"

const variants = cva(
  "absolute bottom-0 right-0 flex h-3.5 w-3.5 items-center justify-center rounded-full",
  {
    variants: {
      variant: {
        default: "invisible",
        online: "bg-green-500",
        offline: "bg-orange-400",
        leader: "bg-container-base h-4 w-4 text-orange-400",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

export interface AvatarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof variants> {
  email?: string
  /** Supplier organisation uid. Used when the consultant has no uploaded photo. */
  companyId?: string
  hideEmail?: boolean
  // The size of the image. Can also be a CSS size value.
  size?: "sm" | "md" | "lg" | "xl" | `${number}${string}`
  overlay?: boolean
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      variant,
      email,
      companyId,
      size = "md",
      overlay = false,
      hideEmail = false,
      ...props
    },
    ref,
  ) => {
    const sizeValue = React.useMemo(() => {
      switch (size) {
        case "sm":
          return "2rem"
        case "md":
          return "2.5rem"
        case "lg":
          return "3rem"
        case "xl":
          return "4rem"
        case "2xl":
          return "6.5rem"
        default:
          return size
      }
    }, [size])
    const style: React.CSSProperties = {
      width: sizeValue,
      height: sizeValue,
      maxHeight: sizeValue,
      maxWidth: sizeValue,
      minHeight: sizeValue,
      minWidth: sizeValue,
    }

    const personFallback = (
      <div
        className={cn(
          "bg-primary-container text-on-primary-container flex h-full w-full items-center justify-center rounded-full",
          props.className,
        )}
        style={style}
      >
        <Icon
          icon={"person"}
          fill={false}
          size={sizeValue}
          maxSize={"4.16667rem"}
          weight={250}
          className="scale-[0.6]"
        />
      </div>
    )

    return (
      <Tooltip
        styles={{ root: { pointerEvents: "none" } }}
        title={hideEmail ? undefined : email}
      >
        <div
          className={cn("group relative flex rounded-full")}
          style={style}
          ref={ref}
        >
          <ImageWithFallback
            src={`https://cdn.flextribe.io/avatars/${email}`}
            alt={email ?? ""}
            className={cn("rounded-full object-cover", props.className)}
            placeholder="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAACXBIWXMAAA7DAAAOwwHHb6hkAAAAGXRFWHRTb2Z0d2FyZQB3d3cuaW5rc2NhcGUub3Jnm+48GgAAB/5JREFUeJzlm2tsVMcVx3/n+oXBZtfG3gXCM4ANNpDwMIVQk6otkRpIW1K1KkgJNA5RVTWBSLRqPyRpy4cokBcJXyIChSgyUhSFVtA2JW2iQqmCcXAI2MSQgHkEdhdj765dG+967+mHtR0Mfq333mugv0/2nbln/udoZvbOzBlRVezE34xHYywxxCxWZbogBQo5Am6FLACBZoWgQKOitSLG5yjVmsJBbxYBO/WJHQHwhSkRzJWoLAWKARmkKQWqVXS/qrF7jItK61TGsSwADQ2MbE/hCUUfA2ZYYvRGlBoR+aMR5Y28PJqsMJl0AC6GyU01zXWGyJMKOVaI6g+BRlP1tYgYWya4aEzKVhIBkECQR1T0RSA/GRFJ0IDKH7xuXgfMwRgYVACuhJlqmroL4b7BNGo5yiERWe1x8WWirxqJvhAIssJUPXLLOA8gLFa0yh/mp4m+mkgADH/YfEVF3wPciTbkANmo7vaFzZdIwK8BDYEaSB8VMneCrExCoINouddlrAGi/dXsNwBx53UP8KA14hxC+YvXLSvoJwj9dRXJDZvbuN2cBxCW+UPmLvrxsc9Cf9h8WVQetVSYo8hKX8h8oc8avQ0BX4gfC/qOLbqcRmSVdyS7eyzqKQCBEFMU/QRw2a3NIUKCzPW4OHNjQU9DQFT1Le4c5wFcpuhOeliU3RQAf4iyW+ojxyJEKfUFuWk+6zYELobJTVOtBfKcFOcggQyVQrebYOeDbj0g1TTXc+c6D+CJGDx1/YOuHhBfz2udU0vaIaQhJSqTOvcTunpAfDPjjnceINdMZW3nP109wB/S48BMJxRU157jn/8+zvmv6kGVCePy+W7pbIoKJjjRPCg1XrcUQ0cAfGFKRLXC7nbbYyZb3tzL+x8d7bH8wW/P58myZaSmJLxKTxhDZW6+m6pUAMFcOfh9y4Hzxlt/63K+9BtFzJ89BYDKz85w8HA1f/2wkoz0FH6xxv6lR0zMVWBUiariD+sJlGI7G7xwqZ6yDVtRU1lXtpzlS0u6le/df4TXduzDSDHYvvmXjBs7yk45AJ96XTLH8DfjQSmyu7VDR06ipjJr+sSbnAd46IESZhZOwIyZ/KfypN1yAO653ESeoTGW4ED/9wXi3x5FheN7rdNZdjkQ7LWOhUhKjCUGajoy86elxie2lpa2Xuu0tka61bUdg2JDhEIn2poyeQwAFVWnaYtEbipvi0Q4fPR0R93RTkhCTbPQEGSaE42VLigia0Qm/vogz2/dQ3NLa1dZc8s1nt+6h8DVINlZmZQusHU+/hqRAvEFzTpgohPtHayoYeOr76CmkjUikxlT70IVTn5xkf+2XEMM4dn1P+GbC2yfkzs5K76geRXIdarFiqrTbHlzH4Gr3Sc6zyg369cup+ReRzpkJ/XiC5ptQLqTrUbbY5w4eY66i/GT70njPMycMZG01BQnZQC0DUkAbiHaDKB5qFUMIU2pQBMOzgHhphbOXQzQGG6hpeUaCAzPzCBn5AgmTfCSPSLTKSkATakC9Wrjr0BzyzUqqk5z+JNajp2s42pj33kN+bku7imexMK5BZTcO43hmRl2SYP4JBgrt+PMr+bUefbuP8KBwzVEou1dzw1DGDd6FG5XFpmZ6aBK67UojaEmvvI1YJpf71EOS0/j/vtm8dDS+RROuctqiaD6tvhDsedU5XdW2WxuaeWFrXv4+Ght/IEIMwvGs3BeIfNmTWHieE+vs320PUbdBT+Vx77g46OnqDl1oats8YIiNvz8+2QNt3CIiDwj/qD+SNF3rbL56427qKo+Q0Z6Kg/cP4cV31vI+LGD22etuxDgz3+vYP+/qohE25lTfDebnlltlVRE5WHxNamHmPqwYEV4rPosGzbuZFh6Gq/8voypHd//yXL67CWefm47bZF2Xnp2DbOLJlth1jQN8RodeXjVVlg8dfYSAIvmT7fMeYBpk8eyaN50AGrPXLLEpsKxMdnUx9edoh9YYbS1Nb7UzRoxzApz3cjOyuzWRrIY6D8AUgFMNcoN9GlLLAPvf3SUDw8dt8ocAJFIv8keCSFqlENHAMa4qPSHOIFF2+LR9hjR9pgVpuxBqcl38yl0BABAkF2Kbk7G7rLvlLBo/vRk5fVJris7aRuCbO/6u/NgpL6ebDNNz/0fnA71fDSWl0eTqfr60OlyBhF59fo8427H4+dD5GSgtQxd6qvd+NNNKczJIdT5oNv26wQXjaj8xnldziAiv7reeeg5R0j8QT2IsNg5afYjcMDjkm8Rv4PQRU8b8Coiq6F7pG5zgiA/4wbnoZc8QY+LLxV53HZZDiEqZT1liEEfiZKjXbyroi/bJ8sZBN3kcfNer+X95ApLIGzuUJU1litzBC33uoxH6OMyxUCyxdP8If0Tt1u+sLLP65aHSTJZGiDqdckPQHdYo8wBVN8eiPMw8IsF7V6X8biiLyanzHZU0E1et/EoA3AeBnFnKBDkh4juuAXXDGFF1o52kVCC96AuTQVC3G2K7hSlNOGXbUDggCJrvC7OJvyuBdfmNgOewRpJkgZBfutxsY0ePnIGQtIXJ4NB3BGDp1R1Hc6dMF0VkS1pMV678ds+USy7OnvlClmaxhNq6GM2ZpydQGWHEWVbfr41Z5q2XJ6+EmROTMxVgiwFZjGI+4kdmMBngn4gapR3bmNZiS0BuJ7LTeSlxFiiBkWoOQOkgPhQcdNxfZ74CXUQaEC1FsP4XEyqYykcHJNNvZ36/geuGCedTPMMMAAAAABJRU5ErkJggg=="
            fill
          >
            {companyId ? (
              <ImageWithFallback
                src={`https://cdn.flextribe.io/organisations/logos/${companyId}`}
                alt=""
                className={cn("rounded-full object-cover", props.className)}
                fill
              >
                {personFallback}
              </ImageWithFallback>
            ) : (
              personFallback
            )}
          </ImageWithFallback>
          {overlay && (
            <div className="bg-primary-container/70 absolute h-8 w-8 rounded-full" />
          )}
          <div className={cn(variants({ variant }))}>
            {variant === "leader" && <OldIcon icon={faStar} size="xs" />}
          </div>
        </div>
      </Tooltip>
    )
  },
)
Avatar.displayName = "Badge"

const CountAvatar = ({
  count,
  size = "md",
}: {
  count: number
  size?: "sm" | "md" | "lg"
}) => (
  <div
    className={cn(
      "text-on-surface relative flex items-center justify-center",
      size === "sm" && "h-8 w-8",
      size === "md" && "h-10 w-10",
      size === "lg" && "h-12 w-12",
    )}
  >
    +{count}
  </div>
)

type AvatarGroupProps = {
  children: React.ReactNode
  maxVisibleAvatars?: number
  overlap?: number
  size?: "sm" | "md" | "lg"
}

const AvatarGroup = ({
  children,
  maxVisibleAvatars = 6,
  overlap = 20,
  size = "md",
}: AvatarGroupProps) => {
  const totalChildren = React.Children.count(children)
  const visibleChildren = React.Children.toArray(children).slice(
    0,
    maxVisibleAvatars,
  )
  const extraCount = totalChildren - maxVisibleAvatars

  const totalWidth =
    (visibleChildren.length + (extraCount > 0 ? 1.8 : 0)) * overlap

  return (
    <div
      className="relative flex items-center"
      style={{ width: `${totalWidth}px` }}
    >
      {visibleChildren.map((child, index) => (
        <div
          key={index}
          className="absolute"
          style={{
            zIndex: visibleChildren.length - index,
            transform: `translateX(${index * overlap}px)`,
          }}
        >
          {child}
        </div>
      ))}
      {extraCount > 0 && (
        <div
          className="text-on-surface-variant absolute right-0"
          style={{
            zIndex: 10,
            // transform: `translateX(${visibleChildren.length * overlap + 10}px)`,
          }}
        >
          <CountAvatar size={size} count={extraCount} />
        </div>
      )}
    </div>
  )
}

export { Avatar, AvatarGroup, CountAvatar }
